import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { Theme } from '../shell/types';

// DESIGN_DEVIATION from plan: keeping v2's `--bg` / `--accent` / `--text` token
// names (defined in styles/tokens.css + .theme-light / .theme-dark variants)
// rather than renaming to `--sd-*`. Every shell component already consumes
// these names via `var(--bg)` etc.; renaming is out-of-scope churn for
// Phase 1 (login). The `--sd-*` compatibility layer from v1 is deferred to
// Phase 2+ when v1 view components start getting ported in.
//
// DESIGN_DEVIATION: dataStyle JSON override (from the v1 <shift-agent> web
// component) isn't parsed yet — accepted as a prop but ignored. Phase 2+
// will wire it if any tenant is actually using it.

const STORAGE_KEY = 'sd-theme-preference';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

function readStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return null;
}

interface ThemeProviderProps {
  children: ReactNode;
  dataTheme?: Theme | null | undefined;
  dataStyle?: string | null | undefined;
  /**
   * Element whose `classList` will carry the `theme-light` / `theme-dark`
   * toggle. In SPA mode pass `document.documentElement`. In Shadow DOM mode
   * pass the `.sd-root` host child so theme variables are scoped to the
   * component and don't leak onto the embedding page.
   */
  themeHostElement?: Element | null | undefined;
}

export function ThemeProvider({ children, dataTheme, themeHostElement }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = readStoredTheme();
    if (stored) return stored;
    return dataTheme === 'dark' ? 'dark' : 'light';
  });

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  const setThemeAndPersist = useCallback((next: Theme) => {
    setTheme(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    const el: Element | null =
      themeHostElement ?? (typeof document !== 'undefined' ? document.documentElement : null);
    if (!el) return;
    el.classList.remove('theme-light', 'theme-dark');
    el.classList.add(`theme-${theme}`);
  }, [theme, themeHostElement]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeAndPersist, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
